import { useEffect, useRef } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { X } from 'lucide-react';
import './Modal.css';

type ModalSize = 'sm' | 'md' | 'lg';
/** Where to position the modal panel on desktop (≥1024px).
 * Mobile always renders as a centered fullscreen sheet regardless of placement. */
type ModalPlacement = 'center' | 'right';

export type ModalProps = {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    size?: ModalSize;
    /** Accessible label — required for screen readers */
    ariaLabel?: string;
    /** If false, clicking the backdrop does not close the modal. Default true. */
    closeOnBackdrop?: boolean;
    /** Desktop placement. Default 'center'. 'right' makes the modal a
     * right-aligned drawer at ≥1024px (used by RoleChangeDrawer). On mobile
     * placement is ignored — the modal always renders full-width centered. */
    placement?: ModalPlacement;
};

type SlotProps = { children: ReactNode; className?: string };

function Modal({
    open,
    onClose,
    children,
    size = 'md',
    ariaLabel,
    closeOnBackdrop = true,
    placement = 'center',
}: ModalProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);

    // Escape to close + focus trap + iOS-safe body scroll lock
    useEffect(() => {
        if (!open) return;

        previouslyFocused.current = document.activeElement as HTMLElement | null;

        const panel = panelRef.current;
        const focusable = panel
            ? Array.from(
                  panel.querySelectorAll<HTMLElement>(
                      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
                  ),
              )
            : [];
        focusable[0]?.focus();

        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
                return;
            }
            if (e.key === 'Tab' && panel) {
                const current = Array.from(
                    panel.querySelectorAll<HTMLElement>(
                        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
                    ),
                );
                if (current.length === 0) return;
                const first = current[0];
                const last = current[current.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKey);

        /* ─── iOS-safe body scroll lock ──────────────────────────────────
         * The previous implementation set `body.style.overflow = 'hidden'`
         * which does NOT stop iOS Safari's rubber-band. We now also pin the
         * body to its current scroll position so the background doesn't
         * jump or scroll while the modal is open, and restore scroll
         * position on unmount. This is the canonical iOS pattern.
         *
         * NOTE: .layout is `position: fixed` for authenticated routes which
         * already prevents most background scrolling — this is belt-and-
         * braces for any modal opened from a non-fixed route (login/signup). */
        const scrollY = window.scrollY;
        const body = document.body;
        const prev = {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width,
        };
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';

        return () => {
            document.removeEventListener('keydown', handleKey);
            body.style.overflow = prev.overflow;
            body.style.position = prev.position;
            body.style.top = prev.top;
            body.style.width = prev.width;
            window.scrollTo(0, scrollY);
            previouslyFocused.current?.focus();
        };
    }, [open, onClose]);

    if (!open) return null;

    const handleBackdrop = (e: MouseEvent<HTMLDivElement>) => {
        if (!closeOnBackdrop) return;
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div
            className={`ui-modal-overlay ui-modal-overlay--${placement}`}
            onClick={handleBackdrop}
        >
            <div
                ref={panelRef}
                className={`ui-modal ui-modal--${size} ui-modal--${placement}`}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
}

Modal.Header = ({
    children,
    className = '',
    onClose,
}: SlotProps & { onClose?: () => void }) => (
    <div className={`ui-modal__header ${className}`.trim()}>
        <h2 className="ui-modal__title">{children}</h2>
        {onClose && (
            <button type="button" className="ui-modal__close" onClick={onClose} aria-label="Close">
                <X size={18} />
            </button>
        )}
    </div>
);

Modal.Body = ({ children, className = '' }: SlotProps) => (
    <div className={`ui-modal__body ${className}`.trim()}>{children}</div>
);

Modal.Footer = ({ children, className = '' }: SlotProps) => (
    <div className={`ui-modal__footer ${className}`.trim()}>{children}</div>
);

export default Modal;
